import React, { useMemo } from "react";
import {
  formatMetric,
  formatNumber,
  formSummary,
  groupMetrics,
  metricLabel,
  resultOf,
  venueSplit,
} from "./fiData";
import "./fi-extra.css";

const chronological = (matches = []) => [...matches].sort((a, b) => String(a.kickoff_utc).localeCompare(String(b.kickoff_utc)));
const shortDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

export function opponentOf(row) {
  const fixture = row.fixture || {};
  if (row.home === true) return fixture.away_team?.name || null;
  if (row.home === false) return fixture.home_team?.name || null;
  return null;
}

/* ------------------------------------------------------------------ */

/** Tiny trend line of one metric over matches; gaps stay gaps. */
export function Sparkline({ values, width = 84, height = 24, label = "Trend" }) {
  const points = values.map((v) => (typeof v === "number" && Number.isFinite(v) ? v : null));
  const present = points.filter((v) => v !== null);
  if (present.length < 2) return <span className="fx-spark" aria-hidden="true" />;
  const min = Math.min(...present);
  const max = Math.max(...present);
  const span = max - min || 1;
  const pad = 3;
  const x = (i) => pad + (i * (width - pad * 2)) / Math.max(1, points.length - 1);
  const y = (v) => height - pad - ((v - min) / span) * (height - pad * 2);
  let path = "";
  let pen = false;
  points.forEach((v, i) => {
    if (v === null) { pen = false; return; }
    path += `${pen ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
    pen = true;
  });
  const lastIndex = points.map((v) => v !== null).lastIndexOf(true);
  return (
    <svg className="fx-spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img"
      aria-label={`${label}: ${present.length} matches, from ${formatNumber(present[0])} to ${formatNumber(present[present.length - 1])}, range ${formatNumber(min)} to ${formatNumber(max)}`}>
      <path d={path} />
      <circle cx={x(lastIndex)} cy={y(points[lastIndex])} r="2.2" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */

export function FormStrip({ matches, detail = 0 }) {
  const ordered = chronological(matches);
  const summary = formSummary(ordered);
  if (!ordered.length) return null;
  const latest = [...ordered].reverse().slice(0, detail);
  return (
    <div>
      <div className="fx-form" role="list" aria-label="Results, oldest to latest">
        {ordered.map((match) => {
          const result = resultOf(match);
          const opponent = opponentOf(match);
          const score = result ? `${match.goals}–${match.conceded}` : "score unavailable";
          return (
            <span
              key={match.fixture_id}
              role="listitem"
              className={`fx-wdl ${result || "none"}`}
              title={`${shortDate(match.kickoff_utc)} · ${match.home ? "Home" : match.home === false ? "Away" : ""}${opponent ? ` vs ${opponent}` : ""} · ${score}`}
            >
              {result || "–"}
            </span>
          );
        })}
      </div>
      {summary.played > 0 && (
        <p className="fi-note" style={{ marginTop: 8 }}>
          Oldest → latest · W{summary.W} D{summary.D} L{summary.L} · {summary.goalsFor} scored, {summary.goalsAgainst} conceded in {summary.played} matches
        </p>
      )}
      {latest.length > 0 && (
        <ul className="fx-results" aria-label="Latest results">
          {latest.map((match) => {
            const result = resultOf(match);
            const opponent = opponentOf(match);
            return (
              <li key={match.fixture_id}>
                <span className={`fx-wdl ${result || "none"}`} style={{ width: 22, height: 22 }}>{result || "–"}</span>
                <span className="fx-results-score">{result ? `${match.goals}–${match.conceded}` : "—"}</span>
                <span>{match.home === true ? "vs" : match.home === false ? "at" : "v"} {opponent || "opponent unavailable"}</span>
                <small>{shortDate(match.kickoff_utc)}</small>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** xG for vs xG against, per match, on one axis. */
export function AttackDefenceTrend({ matches }) {
  const rows = chronological(matches).filter(
    (m) => typeof m.values?.xgf === "number" && typeof m.values?.xga === "number"
  );
  if (rows.length < 2) return <p className="fi-note">At least two matches with both xG for and against are needed for this chart.</p>;

  const W = 640;
  const H = 210;
  const m = { l: 34, r: 12, t: 12, b: 26 };
  const max = Math.max(...rows.flatMap((r) => [r.values.xgf, r.values.xga])) * 1.1 || 1;
  const x = (i) => m.l + (i * (W - m.l - m.r)) / (rows.length - 1);
  const y = (v) => H - m.b - (v / max) * (H - m.t - m.b);
  const line = (key) => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(r.values[key]).toFixed(1)}`).join(" ");
  const mean = (key) => rows.reduce((s, r) => s + r.values[key], 0) / rows.length;
  const ticks = [0, max / 2, max];

  return (
    <div>
      <div className="fx-key">
        <span className="a">● xG for · avg {mean("xgf").toFixed(2)}</span>
        <span className="b">● xG against · avg {mean("xga").toFixed(2)}</span>
      </div>
      <svg className="fx-timeline" viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Expected goals for and against over ${rows.length} matches, from ${shortDate(rows[0].kickoff_utc)} to ${shortDate(rows[rows.length - 1].kickoff_utc)}`}>
        {ticks.map((t) => (
          <g key={t}>
            <line className="axis" x1={m.l} x2={W - m.r} y1={y(t)} y2={y(t)} />
            <text x={m.l - 6} y={y(t) + 4} textAnchor="end">{t.toFixed(1)}</text>
          </g>
        ))}
        <path className="a" d={line("xgf")} />
        <path className="b" d={line("xga")} />
        {rows.map((r, i) => (
          <g key={r.fixture_id}>
            <circle className="goal-a" cx={x(i)} cy={y(r.values.xgf)} r="3.4"><title>{`${shortDate(r.kickoff_utc)} · xG for ${r.values.xgf.toFixed(2)} · xG against ${r.values.xga.toFixed(2)}${opponentOf(r) ? ` · vs ${opponentOf(r)}` : ""}`}</title></circle>
            <circle className="goal-b" cx={x(i)} cy={y(r.values.xga)} r="3.4"><title>{`${shortDate(r.kickoff_utc)} · xG against ${r.values.xga.toFixed(2)}`}</title></circle>
          </g>
        ))}
        <text x={m.l} y={H - 6}>{shortDate(rows[0].kickoff_utc)}</text>
        <text x={W - m.r} y={H - 6} textAnchor="end">{shortDate(rows[rows.length - 1].kickoff_utc)}</text>
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** Home vs away means for headline metrics (sample sizes shown). */
export function VenueSplit({ matches }) {
  const keys = ["xgf", "xga", "shots_for", "shots_against", "corners_for", "corners_against", "territory.field_tilt", "defending.ppda"];
  const split = useMemo(() => venueSplit(matches, keys), [matches]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!split.homeCount || !split.awayCount) {
    return <p className="fi-note">A home/away split needs both home and away matches in the sample ({split.homeCount} home, {split.awayCount} away).</p>;
  }
  const rows = split.rows.filter((r) => r.home || r.away);
  return (
    <div className="fx-scroll" style={{ maxHeight: "none" }}>
      <table>
        <thead><tr><th>Match mean</th><th className="fx-num">Home ({split.homeCount})</th><th className="fx-num">Away ({split.awayCount})</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{metricLabel(row.key)}</td>
              <td className="fx-num">{formatMetric(row.key, row.home?.value, true)}</td>
              <td className="fx-num">{formatMetric(row.key, row.away?.value, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Where the team operates, from the provider's own average heights, plus the
 * direct versus build-up split of its attacks. No scale is invented: markers
 * sit at the supplied metres on a 105 m pitch strip.
 */
export function TacticalProfile({ metrics = {} }) {
  const action = metrics["territory.avg_action_x"]?.value;
  const defensive = metrics["defending.avg_defensive_action_x"]?.value;
  const direct = metrics["tempo.direct_attacks"]?.value;
  const buildup = metrics["tempo.buildup_attacks"]?.value;
  const hasHeights = typeof action === "number" || typeof defensive === "number";
  const hasStyle = typeof direct === "number" && typeof buildup === "number" && direct + buildup > 0;
  if (!hasHeights && !hasStyle) return <p className="fi-note">The provider supplied no tactical-profile metrics for this sample.</p>;
  const directShare = hasStyle ? direct / (direct + buildup) : null;

  return (
    <div>
      {hasHeights && (
        <>
          <svg viewBox="0 0 105 22" className="fx-strip" role="img"
            aria-label={`Average defensive action ${formatNumber(defensive, 1)} metres from own goal line; average action ${formatNumber(action, 1)} metres`}>
            <rect x="0" y="6" width="105" height="10" rx="2" fill="#173f32" />
            <path d="M0 6V16M52.5 6V16M105 6V16" stroke="#c5ded0" strokeOpacity=".5" strokeWidth=".4" />
            {typeof defensive === "number" && (
              <g><line x1={defensive} x2={defensive} y1="3" y2="19" stroke="#f2b66d" strokeWidth="1.2" />
                <text x={defensive} y="2.6" textAnchor="middle" fontSize="3.2" fill="#f2b66d" style={{ fill: "#f2b66d" }}>defensive actions {formatNumber(defensive, 1)} m</text></g>
            )}
            {typeof action === "number" && (
              <g><line x1={action} x2={action} y1="3" y2="19" stroke="#4ddfb0" strokeWidth="1.2" />
                <text x={action} y="21.4" textAnchor="middle" fontSize="3.2" style={{ fill: "#4ddfb0" }}>all actions {formatNumber(action, 1)} m</text></g>
            )}
          </svg>
          <p className="fi-note">Provider x-positions in metres (0–105), match means. Assumed to use the same acting-team frame as shots and heatmaps, where higher means further up the pitch; the bundle does not define these two metrics separately.</p>
        </>
      )}
      {hasStyle && (
        <div style={{ marginTop: 12 }}>
          <div className="fx-meter" role="img" aria-label={`${Math.round(directShare * 100)}% of attacks direct, ${Math.round((1 - directShare) * 100)}% build-up`}>
            <i style={{ width: `${directShare * 100}%`, background: "var(--fx-away)" }} />
            <i style={{ width: `${(1 - directShare) * 100}%`, background: "var(--fx-home)" }} />
          </div>
          <div className="fx-meter-legend">
            <span>Direct attacks <b>{formatNumber(direct, 1)}</b> / match</span>
            <span>Build-up attacks <b>{formatNumber(buildup, 1)}</b> / match</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Grouped metric cards. Each row shows the value, the sample behind it and a
 * per-match trend of the underlying real values.
 */
export function MetricGroups({ kind, metrics, matches, per90 = false, query = "" }) {
  const groups = useMemo(() => groupMetrics(kind, metrics), [kind, metrics]);
  const series = useMemo(() => chronological(matches), [matches]);
  const needle = query.trim().toLowerCase();
  const player = kind === "player";

  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((item) => !needle || item.label.toLowerCase().includes(needle) || item.key.toLowerCase().includes(needle)) }))
    .filter((g) => g.items.length);

  if (!visible.length) return <p className="fi-note" style={{ padding: "0 28px" }}>No metrics match “{query}”.</p>;

  return (
    <div className="fx-three">
      {visible.map((group) => (
        <article className="fi-card" key={group.id}>
          <h3>{group.title}</h3>
          <dl className="fx-metric-list">
            {group.items.map(({ key, label, hint, metric }) => {
              const value = player && per90 ? metric.per90 : metric.value;
              const spark = series.map((row) => row.values?.[key]);
              return (
                <div className="fx-metric" key={key}>
                  <dt>
                    <span className={hint ? "fx-hint" : undefined} title={hint || undefined}>{label}</span>
                    <small>
                      {player && per90 ? `per 90 · ${Math.round(metric.minutes || 0)} min` : metric.unit} · {metric.sample} {metric.sample === 1 ? "match" : "matches"}
                    </small>
                  </dt>
                  <dd>{formatMetric(key, value, !player)}</dd>
                  <Sparkline values={spark} label={`${label} per match`} />
                </div>
              );
            })}
          </dl>
        </article>
      ))}
    </div>
  );
}
